import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { track } from "@/lib/track";
import { useLocalizedHref } from "@/hooks/useLocalizedHref";
import { useLocale } from "@/hooks/useLocale";
import { DEFAULT_LOCALE } from "@translations/config";

interface NavLink {
  /** Translation key under `common.nav.*` */
  labelKey: string;
  /** Path-like href: starts with `/` for routes, `#` for landing-page anchors. */
  href: string;
}

const links: NavLink[] = [
  { labelKey: "common.nav.services", href: "#services" },
  { labelKey: "common.nav.process", href: "/process" },
  { labelKey: "common.nav.work", href: "/work" },
  { labelKey: "common.nav.about", href: "/about" },
  { labelKey: "common.nav.pricing", href: "#pricing" },
  { labelKey: "common.nav.faq", href: "#faq" },
];

function NavItem({ link, onClick }: { link: NavLink; onClick?: () => void }) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const localizedHref = useLocalizedHref();
  const locale = useLocale();
  const label = t(link.labelKey);

  if (link.href.startsWith("/")) {
    return (
      <Link to={localizedHref(link.href)} onClick={onClick}>
        {label}
      </Link>
    );
  }

  // Hash link: when off the landing page, route back to the locale's home
  // + hash so the browser lands and scrolls to the section instead of
  // looking for the anchor on the current page (e.g., /work#services
  // would 404 the anchor).
  const landingPath = locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
  const isOnLanding = pathname === landingPath;
  const href = link.href.startsWith("#") && !isOnLanding ? `${landingPath}${link.href}` : link.href;

  return (
    <a href={href} onClick={onClick}>
      {label}
    </a>
  );
}

export function Navbar() {
  const [open, setOpen] = React.useState(false);
  const { t } = useTranslation();
  const localizedHref = useLocalizedHref();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const locale = useLocale();

  // "Let's talk" scrolls to the homepage CTA band; from other pages it
  // routes back to the landing page + hash (same pattern as NavItem).
  const landingPath = locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
  const ctaHref = pathname === landingPath ? "#cta" : `${landingPath}#cta`;

  return (
    <div className="nav-wrap">
      <nav className="nav-pill">
        <Link to={localizedHref("/")} className="nav-brand" aria-label="Holy Moly">
          <span className="wordmark">
            holy<em>moly</em>
          </span>
        </Link>

        <div className="nav-links">
          {links.map((l) => (
            <NavItem key={l.labelKey} link={l} />
          ))}
        </div>

        <div className="nav-right">
          <LocaleSwitcher className="hidden md:inline-flex" />
          <a
            href={ctaHref}
            onClick={() => track("cta_scroll", { source: "navbar" })}
            className="btn-orange"
          >
            {t("common.nav.letsTalk")}
            <span className="pip">
              <Icon.ChevronRight />
            </span>
          </a>
          <button
            className="icon-btn mobile-only"
            aria-label={t("common.nav.menu")}
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <Icon.X /> : <Icon.Menu />}
          </button>
        </div>

        {open && (
          <div className="mobile-panel">
            {links.map((l) => (
              <NavItem key={l.labelKey} link={l} onClick={() => setOpen(false)} />
            ))}
            <LocaleSwitcher className="mt-2" />
          </div>
        )}
      </nav>
    </div>
  );
}
